/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * A staffel as a ladder: one row per band, the kWh range on the left and the
 * band's % right-aligned beside it — "tot 3.500 kWh · 25%", "3.500 – 5.000 kWh
 * · 22%", …, "vanaf 8.000 kWh · 14%" (Jasper 2026-10-15: "nicely represent the
 * ladder"). ONE drawing for every surface that shows a staffel's bands: the
 * verdienmodel aandachtspunt in the comparer's panel, on the profile page and
 * in a compare page's cell (src/components/terms/DerivedBullets.tsx), and the
 * admin's review card (PendingUpdateReviewCard).
 *
 * It holds no copy and no number format: the rows come worded —
 * `staffelLadderRows` (src/lib/offerPricingText.ts) on the public site,
 * `staffelLadderNl` (src/lib/offerPricingDescribe.js) in the Dutch-only admin
 * — so the bands, their boundaries and their words are the editors'. The row
 * marked `current` (the band the surface's volume falls in) takes
 * `currentClassName` and `aria-current`, so the emphasis is not colour alone.
 *
 * A `<dl>` on a two-column grid, each row a subgrid: the columns line up
 * across the rows, the % column is as wide as its widest figure, tabular
 * numbers keep the digits in step, and no border or fill — it sits in a
 * bullet's text. Where a column is narrower than a range (a compare page's
 * two columns on a 375 px phone leave 120 px; "3.500 – 5.000 kWh  22%" needs
 * about 130) the range wraps balanced — "3.500 –" over "5.000 kWh" — never a
 * lone "kWh"; the % never wraps. The size is the caller's (`className`). Pure markup: the baked
 * HTML and the hydrating client agree.
 */

import React from 'react';
import { cn } from '../../lib/cn';
import type { StaffelLadderRow } from '../../lib/offerPricingText';

export interface StaffelLadderProps {
  rows: readonly StaffelLadderRow[];
  /** The ladder's own classes: its size, colour and spacing above. */
  className?: string;
  /** Classes of every row that is not the current one. */
  rowClassName?: string;
  /** Classes of the current row (the band at the surface's volume). */
  currentClassName?: string;
}

export const StaffelLadder: React.FC<StaffelLadderProps> = ({
  rows,
  className,
  rowClassName,
  currentClassName,
}) => (
  <dl
    data-staffel-ladder=""
    className={cn(
      'grid w-max max-w-full grid-cols-[minmax(0,1fr)_auto] gap-x-2 gap-y-px tabular-nums sm:gap-x-3',
      className
    )}
  >
    {rows.map((row) => (
      <div
        key={row.range}
        aria-current={row.current ? 'true' : undefined}
        className={cn(
          'col-span-2 grid grid-cols-subgrid',
          row.current ? currentClassName : rowClassName
        )}
      >
        <dt className="min-w-0 text-balance">{row.range}</dt>
        <dd className="whitespace-nowrap text-right">{row.pct}</dd>
      </div>
    ))}
  </dl>
);
